import { engineComponent, EngineComponentFactory, Logger, NumberUtils } from "neo-common";
import { ClientHost, Communication, GameEngine } from "neo-games";
import { FrameTimerEventEmitter, CreateFrameTimer, FrameTimerEvents } from "../../Utils/FrameTimer";
import { ComponentTypes } from "../../Components/ComponentTypes";
import { BaseActions } from "../Common/Actions/BaseActions";
import { UserControls } from "../Common/UserControls/UserControls";
import { HtmlController } from "../../HTMLComponents/HtmlController";
import { GameManager } from "../../Managers/GameManager";
import { GameData } from "../Common/Datas/GameData";
import { GameActions } from "../Common/Actions/GameActions";
import { BaseController } from "../../GameComponents/Base/BaseController";
import { AppContext } from "../../../Common/AppContext";
import { BetManager } from "../../Managers/BetManager";
import { ModuleManager } from "../../Managers/ModuleManager";
import { DataManager } from "../../Managers/DataManager";
import { ActionsTypes, ActionSet } from "../Common/Actions/ActionSet";
import { BalanceManager } from "../../Managers/BalanceManager";
import { LimitDialog } from "../Common/Dialogs/LimitDialog";
import { ResponsibleDialog1 } from "../Common/Dialogs/ResponsibleDialog1";
import { TerminalActions } from "../Common/Actions/TerminalActions";
import { PlayList } from "../../Components/Sounds/PlayList";
import { FireBirdPaytable } from "./FireBirdPaytable";
import { FireBirdHelp } from "./FireBirdHelp";
import { StyxClientHostEvents } from "./FireBirdTypes";
import { Game } from "neo-games/Engine/Communication/index";

var GameModules = Communication.Game.GameModules;
var GameEvents = ClientHost.GameEvents;

@engineComponent(ComponentTypes.HtmlElement)
export class FireBirdBaseUserControls extends UserControls {
    protected ledStripe: string;

    protected _controller: BaseController;
    protected _terminalActions: ActionSet;

    private _userControls: HTMLElement;
    private _userControls_supreme: HTMLElement;
    private _paytable: FireBirdPaytable;
    private _help: FireBirdHelp;
    private _afterWinDelay: FrameTimerEventEmitter;
    private _autoplayDelay: FrameTimerEventEmitter;
    private _autoplayAfterGamble: boolean = false;
    private _autoplayAfterFeature: boolean = false;

    private _unColectedWin: number = 0;
    private _unColectedWinDelay: FrameTimerEventEmitter;

    private _featureAutoStartTimer: FrameTimerEventEmitter;
    private _featureAutoStarted: boolean = false;

    private _wasPrimaryGameEnded: boolean = true;
    private _wasGameEnded: boolean = true;

    constructor() {
        super("base");
    }

    create(node: Element): void {
        super.create(node);

        this.ledStripe = node.hasAttribute("LedStripe") ? node.getAttribute("LedStripe") : "255;255;255";

        this._terminalActions = new ActionSet(
            `${this._key}.terminalActions`,
            EngineComponentFactory.getComponentName(this),
        );

        this.registerActions();

        this._afterWinDelay = CreateFrameTimer("afterWinDelay");
        this._afterWinDelay.on(FrameTimerEvents.Complete, this.prepareNewGameRound.bind(this));

        this._autoplayDelay = CreateFrameTimer("autoplayDelay");
        this._autoplayDelay.on(FrameTimerEvents.Complete, this.autoplayDelayed.bind(this));

        this._unColectedWinDelay = CreateFrameTimer("unColectedWinDelay");
        this._unColectedWinDelay.on(FrameTimerEvents.Complete, this.unColectedWinDelay.bind(this));

        this._userControls = document.getElementById("userControls");

        this._featureAutoStartTimer = CreateFrameTimer("featureAutoStartTimer");
        this._featureAutoStartTimer.on(FrameTimerEvents.Complete, this.featureAutoStartDelayed.bind(this));

        this.handleClientHostReload = this.handleClientHostReload.bind(this);

        // event 'reload' is fired when client host requests reload of actions (styx/firebird only)
        AppContext.clientHost.on(StyxClientHostEvents.Reload, this.handleClientHostReload);

        let creditBox: HTMLElement = document.getElementById("creditBox");
        let betBox: HTMLElement = document.getElementById("betBox");

        if (AppContext != null && AppContext.gameConfiguration != null) {
            if (creditBox != null) {
                if (AppContext.gameConfiguration.UseCoins) {
                    creditBox.classList.add("denomOn");
                }
                if (AppContext.gameConfiguration.HideAmountsEnabled) {
                    creditBox.classList.add("hideAmountsOn");
                }
            }
            if (betBox != null) {
                if (AppContext.gameConfiguration.HideAmountsEnabled) {
                    betBox.classList.add("hideAmountsOn");
                }
            }
        }
    }

    private handleClientHostReload() {
        if (ModuleManager.gambleActive()) {
            return;
        }
        if (ModuleManager.freespinsEnabled() || ModuleManager.freespinsActive()) {
            return;
        }

        if (DEBUG) {
            Logger.debugGroup("FireBirdBaseUserControls", `Event 'reload' arrived, actions updated...`);
        }

        AppContext.clientHost.setLEDStripe(this.ledStripe, 0);
        AppContext.clientHost.actionsUpdated(this.getAllTerminalActions());
    }

    protected get terminalActions(): ActionSet {
        return this._terminalActions;
    }

    public getAllTerminalActions(): ActionsTypes {
        return this._terminalActions.getAllActions();
    }

    addController(controller: BaseController): void {
        this._controller = controller;
        this._controller.on(BaseController.SpinReceived, this.spinReceived.bind(this));
        this._controller.on(BaseController.MachineStopped, this.machineStopped.bind(this));
        this._controller.on(BaseController.WinCalculationFinished, this.winCalculationFinished.bind(this));
        this._controller.on(BaseController.AllWinsFinished, this.allWinsFinished.bind(this));
        this._controller.on(BaseController.IntroFinished, this.introFinished.bind(this));
        this._controller.on(BaseController.OutroFinished, this.outroFinished.bind(this));
    }

    registerActions(): void {
        this.actions.registerActions({
            [BaseActions.ActionAutoplay]: false,
            [BaseActions.ActionAutoplayStop]: false,
            [BaseActions.ActionBaseCollect]: false,
            [BaseActions.ActionBetCycle]: false,
            [BaseActions.ActionDenomSelect]: false,
            [BaseActions.ActionCoinCountSelect]: false,
            [BaseActions.ActionBetSelect]: false,
            [BaseActions.ActionSpin]: false,
            [BaseActions.ActionSpinStop]: false,
            [BaseActions.ActionSelectPayline + 1]: false,
            [BaseActions.ActionSelectPayline + 2]: false,
            [BaseActions.ActionSelectPayline + 3]: false,
            [BaseActions.ActionSelectPayline + 4]: false,
            [BaseActions.ActionSelectPayline + 5]: false,
            [BaseActions.ActionSelectPayline + 6]: false,
            [BaseActions.ActionSelectPayline + 7]: false,
            [BaseActions.ActionSelectPayline + 8]: false,
            [BaseActions.ActionSelectPayline + 9]: false,
            [BaseActions.ActionSelectPayline + 10]: false,
            [BaseActions.ActionSelectPayline + 11]: false,
            [BaseActions.ActionSelectPayline + 12]: false,
            [BaseActions.ActionSelectPayline + 13]: false,
            [BaseActions.ActionSelectPayline + 14]: false,
            [BaseActions.ActionSelectPayline + 15]: false,
        });

        this.terminalActions.registerActions({
            [TerminalActions.Payout]: false,
            [TerminalActions.BackToLobby]: false,
            [TerminalActions.StartWithCollect]: false,
            [TerminalActions.Autoplay]: false,
            [TerminalActions.BetOrGamble]: false,
        });
    }

    initReceived(): void {
        this._userControls_supreme = document.getElementById("userControls_supreme");

        super.initReceived();

        this._controller.initReceived();
        this._controller.disableLogo();

        this._paytable = new FireBirdPaytable();
        this._paytable.initialize();

        this._help = new FireBirdHelp();
        this._help.initialize();

        if (GameManager.gameResponse != null) {
            DataManager.updateGameDataFromModule(GameData.DataTotalWin, GameManager.gameResponse.totalWin.toString());
            if (AppContext.gameConfiguration.UseCoins != null && BetManager.CoinSize != null) {
                DataManager.updateGameDataFromModule(
                    GameData.DataTotalWinDenom,
                    parseInt(
                        NumberUtils.devide(GameManager.gameResponse.totalWin, BetManager.CoinSize).toString(),
                    ).toString(),
                );
            }
        }

        if (this._userControls != null && !ModuleManager.gambleActive()) {
            this._userControls.style.display = "none";
        }
        if (
            this._userControls != null &&
            !ModuleManager.gambleActive() &&
            !this._userControls.classList.contains(HtmlController.DisabledClass)
        ) {
            this._userControls.classList.add(HtmlController.DisabledClass);
        }

        if (this._userControls_supreme != null && !ModuleManager.gambleActive()) {
            this._userControls_supreme.style.display = "none";
        }

        DataManager.updateDataElements();

        this._paytable.changeBet();
    }

    collectReceived(): void {
        if (GameManager.disableOnCollect) {
            GameManager.disableGamePlay = true;
        }

        DataManager.gameState("GameEnded");
        this._wasGameEnded = true;
        super.collectReceived();

        DataManager.Collectable = true;
        DataManager.updateDataElements();

        this.prepareNewGameRound();

        this._paytable.reset();
        this._help.reset();

        DataManager.updateDataElement(GameData.DataTotalWin, this._unColectedWin.toString());
        if (AppContext.gameConfiguration.UseCoins != null && BetManager.CoinSize != null) {
            DataManager.updateDataElement(
                GameData.DataTotalWinDenom,
                parseInt(NumberUtils.devide(this._unColectedWin, BetManager.CoinSize).toString()).toString(),
            );
        }
        this._unColectedWinDelay.stop();
        this._unColectedWinDelay.start(75);
        this._unColectedWin = 0;
    }

    restoreGameRound(): void {
        super.collectReceived();

        DataManager.Collectable = true;

        this.prepareNewGameRound();
    }

    spinReceived(): void {
        DataManager.gameState("PrimaryGameStarted");
        if (
            DataManager.Autoplay &&
            AppContext.gameConfiguration.AutoplayModeSpeed === "fast" &&
            AppContext.gameConfiguration.GameEnableReelsStop &&
            AppContext.gameConfiguration.MinGameRoundDuration === 0
        ) {
            this.stop();
        } else {
            this.actions.changeActionState(
                BaseActions.ActionSpinStop,
                AppContext.gameConfiguration.GameEnableReelsStop,
            );
            this.terminalActions.changeActionState(
                TerminalActions.StartWithCollect,
                AppContext.gameConfiguration.GameEnableReelsStop,
            );
            HtmlController.updateActionElements(this.getAllActions());
            AppContext.clientHost.actionsUpdated(this.getAllTerminalActions());
        }
        DataManager.updateDataElement(GameData.DataRoundId);
    }

    dispatchAction(action: string): void {
        if (ModuleManager.gambleActive() || ModuleManager.freespinsActive()) {
            return;
        }

        if (action === BaseActions.ActionDenomSelect && this.actions.getActionState(action)) {
            if (BetManager.DenomContainer != null) {
                BetManager.DenomContainer.toggle();
            }
            return;
        }

        if (action === BaseActions.ActionCoinCountSelect && this.actions.getActionState(action)) {
            if (BetManager.CoinCountContainer != null) {
                BetManager.CoinCountContainer.toggle();
            }
            return;
        }

        if (action === BaseActions.ActionBetSelect && this.actions.getActionState(action)) {
            if (BetManager.BetContainer != null) {
                BetManager.BetContainer.toggle();
            }
            return;
        }

        let denomSelectValue: string = "";
        if (
            action.substr(0, BaseActions.ActionDenomSelectValue.length) === BaseActions.ActionDenomSelectValue &&
            this.actions.getActionState(BaseActions.ActionDenomSelect)
        ) {
            denomSelectValue = action.substr(BaseActions.ActionDenomSelectValue.length, action.length);
            action = BaseActions.ActionDenomSelect;
        }

        let coinCountSelectValue: string = "";
        if (
            action.substr(0, BaseActions.ActionCoinCountSelectValue.length) ===
                BaseActions.ActionCoinCountSelectValue &&
            this.actions.getActionState(BaseActions.ActionCoinCountSelect)
        ) {
            coinCountSelectValue = action.substr(BaseActions.ActionCoinCountSelectValue.length, action.length);
            action = BaseActions.ActionCoinCountSelect;
        }

        let betSelectValue: string = "";
        if (
            action.substr(0, BaseActions.ActionBetSelectValue.length) === BaseActions.ActionBetSelectValue &&
            this.actions.getActionState(BaseActions.ActionBetSelect)
        ) {
            betSelectValue = action.substr(BaseActions.ActionBetSelectValue.length, action.length);
            action = BaseActions.ActionBetSelect;
        }

        if (action.substr(0, BaseActions.ActionSelectPayline.length) === BaseActions.ActionSelectPayline) {
            this._controller.selectPayline();
            return;
        }

        if (action === GameActions.ActionSpinOrStop) {
            if (this.actions.getActionState(BaseActions.ActionSpin)) {
                action = BaseActions.ActionSpin;
            } else if (this.actions.getActionState(BaseActions.ActionSpinStop)) {
                action = BaseActions.ActionSpinStop;
            } else {
                return;
            }
        }

        if (action === TerminalActions.StartWithCollect) {
            if (this.actions.getActionState(BaseActions.ActionBaseCollect)) {
                action = BaseActions.ActionBaseCollect;
            } else if (this.actions.getActionState(BaseActions.ActionSpin)) {
                action = BaseActions.ActionSpin;
            } else if (this.actions.getActionState(BaseActions.ActionSpinStop)) {
                action = BaseActions.ActionSpinStop;
            }
        }

        if (action === TerminalActions.Autoplay) {
            if (this.actions.getActionState(BaseActions.ActionAutoplay)) {
                action = BaseActions.ActionAutoplay;
                this._help.reset();
            } else if (this.actions.getActionState(BaseActions.ActionAutoplayStop)) {
                action = BaseActions.ActionAutoplayStop;
                this._help.reset();
            }
        }

        if (action === TerminalActions.BetOrGamble) {
            if (this.actions.getActionState(BaseActions.ActionBetCycle)) {
                action = BaseActions.ActionBetCycle;
            }
        }

        if (this.actions.getActionState(action)) {
            this._help.reset();
            PlayList.play("SND_Click");
            super.dispatchAction(action);

            switch (action) {
                case BaseActions.ActionSpin:
                    this.spin();
                    this._help.reset();
                    break;
                case BaseActions.ActionSpinStop:
                    this.stop();
                    break;
                case BaseActions.ActionAutoplay:
                    if (!DataManager.Autoplay) {
                        DataManager.gameState("AutoPlayStarted");
                    }
                    this.autoplay();
                    break;
                case BaseActions.ActionAutoplayStop:
                    this.autoplayStop();
                    break;
                case BaseActions.ActionBetCycle:
                    this.changeBet(false, false, true);
                    if (BetManager.DenomContainer != null) {
                        BetManager.DenomContainer.disable();
                    }
                    if (BetManager.CoinCountContainer != null) {
                        BetManager.CoinCountContainer.disable();
                        BetManager.CoinCountContainer.changeValue();
                    }
                    if (BetManager.BetContainer != null) {
                        BetManager.BetContainer.disable();
                        BetManager.BetContainer.changeValue();
                    }
                    break;
                case BaseActions.ActionBaseCollect:
                    this.collect();
                    break;
                case BaseActions.ActionDenomSelect:
                    if (denomSelectValue != "") {
                        this.changeDenom(parseFloat(denomSelectValue));
                    }
                    break;
                case BaseActions.ActionCoinCountSelect:
                    if (coinCountSelectValue != "") {
                        this.changeCoinCount(parseFloat(coinCountSelectValue));
                    }
                    break;
                case BaseActions.ActionBetSelect:
                    if (betSelectValue != "") {
                        this.changeBetByValue(parseFloat(betSelectValue));
                    }
                    break;
            }
        }
    }

    collect(): void {
        super.collect();

        ModuleManager.disableGamble();
        this.disable();
        this.playCollectSound();

        this._controller.collect();
        if (!this._wasPrimaryGameEnded) {
            DataManager.gameState("PrimaryGameEnded");
            this._wasPrimaryGameEnded = true;
        }
        if (GameManager.gameResponse.totalWin > 0) {
            DataManager.gameState("GameCollectEscrow");
        }

        LimitDialog.closeDialog();
        GameManager.collect(GameModules.Base);

        if (AppContext.gameConfiguration.UseCoins != null && BetManager.CoinSize != null) {
            DataManager.updateDataElement(
                GameData.DataTotalWinDenom,
                parseInt(NumberUtils.devide(this._unColectedWin, BetManager.CoinSize).toString()).toString(),
            );
        }
        DataManager.updateDataElement(GameData.DataTotalWin, this._unColectedWin.toString());
        this._unColectedWinDelay.start(75);
    }

    unColectedWinDelay(): void {
        DataManager.updateDataElement(GameData.DataTotalWinDenom, "0");
        DataManager.updateDataElement(GameData.DataTotalWin, "0");
    }

    spin(): void {
        if (GameManager.disableOnCollect) {
            if (GameManager.gameResponse.totalWin > 0) {
                this.collect();
            } else {
                GameManager.disableGamePlay = true;
            }
            return;
        }

        if (!BalanceManager.hasCredit(false, true)) {
            if (DataManager.Autoplay) {
                DataManager.gameState("AutoPlayEnded");
            }
            DataManager.Autoplay = false;
            ModuleManager.disableGamble();
            // TODO FireBirdPaytable
            // PaytableClient.sendGameAction('AUTOPLAY_STOP');
            this.prepareNewGameRound();
            return;
        }

        DataManager.Collectable = false;
        ModuleManager.disableGamble();
        HtmlController.dispatchUiAction("");
        this.actions.disableAllActions();
        this.terminalActions.disableAllActions();
        if (DataManager.Autoplay) {
            this.actions.changeActionState(BaseActions.ActionAutoplayStop, DataManager.autoplayEnabled());
            this.terminalActions.changeActionState(TerminalActions.Autoplay, DataManager.autoplayEnabled());
        }
        HtmlController.updateActionElements(this.getAllActions());
        AppContext.clientHost.actionsUpdated(this.getAllTerminalActions());
        this.playCollectSound();

        BalanceManager.fixBalance();
        if (!DataManager.GameRoundClosed) {
            DataManager.updateDataElement(GameData.DataTotalWin);
            DataManager.updateDataElement(GameData.DataTotalWinDenom);
        }

        if (!this._wasPrimaryGameEnded) {
            DataManager.gameState("PrimaryGameEnded");
            this._wasPrimaryGameEnded = true;
        }
        if (!this._wasGameEnded) {
            DataManager.gameState("GameEnded");
            this._wasGameEnded = true;
        }

        DataManager.gameState("PrimaryGameEscrow");
        this._controller.spin();

        LimitDialog.closeDialog();

        this._paytable.reset();
        this._help.reset();
    }

    stop(): void {
        this.actions.changeActionState(BaseActions.ActionSpinStop, false);
        this.terminalActions.changeActionState(TerminalActions.StartWithCollect, false);
        HtmlController.updateActionElements(this.getAllActions());
        AppContext.clientHost.actionsUpdated(this.getAllTerminalActions());
        HtmlController.dispatchUiAction("");
        this._controller.stop();
        this._help.reset();
    }

    autoplayDelayed(): void {
        this._autoplayDelay.stop();
        if (DataManager.Autoplay) {
            this.autoplay();
        } else {
            this.prepareNewGameRound();
        }
    }

    autoplay(): void {
        this._afterWinDelay.stop();

        if (DataManager.AutoplayCount === "∞") {
            DataManager.Autoplay = true;
            DataManager.updateDataElement(GameData.DataAutoplayCount, DataManager.AutoplayCount);
            this.spin();
        } else {
            DataManager.AutoplayCount = (parseInt(DataManager.AutoplayCount) - 1).toString();
            if (parseInt(DataManager.AutoplayCount) >= 0) {
                DataManager.Autoplay = true;
                DataManager.updateDataElement(GameData.DataAutoplayCount, DataManager.AutoplayCount);
                this.spin();
            } else {
                if (DataManager.Autoplay) {
                    DataManager.gameState("AutoPlayEnded");
                }
                DataManager.Autoplay = false;
                this.prepareNewGameRound();
            }
        }
    }

    autoplayStop(): void {
        if (DataManager.Autoplay) {
            DataManager.gameState("AutoPlayEnded");
        }
        DataManager.Autoplay = false;
        this.actions.changeActionState(BaseActions.ActionAutoplayStop, false);
        this.terminalActions.changeActionState(TerminalActions.Autoplay, false);
        HtmlController.updateActionElement(BaseActions.ActionAutoplayStop, false);
        AppContext.clientHost.actionsUpdated(this.getAllTerminalActions());
    }

    changeBetByValue(value: number): void {
        if (!DataManager.GameRoundClosed) {
            this.collect();
        } else {
            this._controller.collect();
            DataManager.updateDataElement(GameData.DataTotalWin, "0");
            DataManager.updateDataElement(GameData.DataTotalWinDenom, "0");
            LimitDialog.closeDialog();
        }
        BetManager.changeBetByValue(value);
        this._paytable.changeBet();
    }

    changeCoinCount(value: number): void {
        if (!DataManager.GameRoundClosed) {
            this.collect();
        } else {
            this._controller.collect();
            DataManager.updateDataElement(GameData.DataTotalWin, "0");
            DataManager.updateDataElement(GameData.DataTotalWinDenom, "0");
            LimitDialog.closeDialog();
        }
        BetManager.changeCoinCount(value);
        this._paytable.changeBet();
    }

    changeDenom(value: number): void {
        if (!DataManager.GameRoundClosed) {
            this.collect();
        } else {
            this._controller.collect();
            DataManager.updateDataElement(GameData.DataTotalWin, "0");
            DataManager.updateDataElement(GameData.DataTotalWinDenom, "0");
            LimitDialog.closeDialog();
        }
        BetManager.changeDenom(value);
        this._paytable.changeBet();
    }

    changeBet(decrease: boolean = false, max: boolean = false, cycle: boolean = false): void {
        if (!DataManager.GameRoundClosed) {
            this.collect();
        } else {
            this._controller.collect();
            DataManager.updateDataElement(GameData.DataTotalWin, "0");
            DataManager.updateDataElement(GameData.DataTotalWinDenom, "0");
            LimitDialog.closeDialog();
        }
        BetManager.changeBet(decrease, max, cycle);
        this._paytable.changeBet();
    }

    machineStopped(): void {
        this._wasGameEnded = false;
        this._wasPrimaryGameEnded = false;

        this._unColectedWinDelay.stop();
        this._unColectedWin = GameManager.gameResponse.totalWin;

        if (ModuleManager.gambleActive()) {
            this.disable();
            return;
        }

        if (ModuleManager.freespinsEnabled()) {
            if (GameManager.baseResponse.wins != null) {
                this._paytable.showWins(GameManager.baseResponse.wins);
            }
            this.disable();
            return;
        }

        // TODO TerminalApi
        /*
        if (AppContext.clientHost) {
            AppContext.clientHost.notifyEventFromGame('GameRoundVisuallyEnded');
        }
        */

        if (GameManager.baseResponse.totalWin === 0) {
            DataManager.gameState("PrimaryGameEnded");
            DataManager.gameState("GameEnded");
            this._wasPrimaryGameEnded = true;
            this._wasGameEnded = true;
            DataManager.updateDataElements();
            if (DataManager.GameRoundClosed && GameManager.disableOnCollect) {
                GameManager.disableGamePlay = true;
            }
        }

        if (
            GameManager.gameResponse.balance != null &&
            GameManager.gameResponse.balance.bonus != null &&
            GameManager.gameResponse.balance.bonus < BetManager.Bet
        ) {
            this.changeBetColor();
        }

        if (GameManager.baseResponse.wins != null) {
            this._paytable.showWins(GameManager.baseResponse.wins);
        }
        if (GameManager.baseResponse.totalWin === 0) {
            DataManager.Collectable = true;
        }

        this.actions.changeActionState(BaseActions.ActionSpinStop, false);
        this.terminalActions.changeActionState(TerminalActions.StartWithCollect, false);
        if (GameManager.baseResponse.totalWin > 0) {
            if (DataManager.Autoplay) {
                if (!DataManager.GameLimit) {
                    this.actions.changeActionState(BaseActions.ActionSpin, true);
                    this.terminalActions.changeActionState(TerminalActions.StartWithCollect, true);
                }
                if (ModuleManager.gambleEnabled()) {
                    this.terminalActions.changeActionState(TerminalActions.BetOrGamble, true);
                }
            }
            this._afterWinDelay.start(30);
        } else {
            this.prepareNewGameRound();
        }

        HtmlController.updateActionElements(this.getAllActions());
        AppContext.clientHost.actionsUpdated(this.getAllTerminalActions());

        if (DataManager.Autoplay && GameManager.baseResponse.totalWin === 0) {
            this._autoplayDelay.start(15);
        }

        ModuleManager.enableGamble();
    }

    winCalculationFinished(): void {
        if (ModuleManager.gambleActive()) {
            this.disable();
            return;
        }

        if (ModuleManager.freespinsEnabled()) {
            this._paytable.bounceLogo();
            DataManager.updateDataElement(GameData.DataTotalWin);
            DataManager.updateDataElement(GameData.DataTotalWinDenom);
            return;
        }

        this._afterWinDelay.stop();

        DataManager.updateDataElement(GameData.DataTotalWin);
        DataManager.updateDataElement(GameData.DataTotalWinDenom);

        if (DataManager.GameRoundClosed) {
            DataManager.updateDataElements();
        }
        DataManager.Collectable = true;
        this.prepareNewGameRound();

        this._paytable.bounceLogo();
        ModuleManager.enableGamble();
    }

    allWinsFinished(): void {
        this._unColectedWin = 0;
        if (ModuleManager.gambleActive()) {
            this.disable();
            return;
        }

        if (!ModuleManager.freespinsEnabled()) {
            DataManager.gameState("PrimaryGameEnded");
            this._wasPrimaryGameEnded = true;
        }
        if (DataManager.GameRoundClosed) {
            DataManager.gameState("GameEnded");
            this._wasGameEnded = true;
        }

        if (ModuleManager.freespinsEnabled()) {
            ModuleManager.enableFreespins();
            this.startFeatureAutoStart();
            return;
        }

        this._afterWinDelay.stop();
        this.prepareNewGameRound();
        if (DataManager.Autoplay) {
            DataManager.gameState("GameEnded");
            this._wasGameEnded = true;
            this.autoplay();
        }
    }

    initIntro(): void {
        AppContext.clientHost.setLEDStripe(this.ledStripe, 2);

        super.initIntro();
        ModuleManager.enableGamble();
        if (GameManager.baseResponse.wins != null) {
            this._paytable.showWins(GameManager.baseResponse.wins);
        }
        this._controller.initIntro();
        if (this._userControls != null) {
            this._userControls.style.display = "block";
        }

        if (this._userControls != null && this._userControls.classList.contains(HtmlController.DisabledClass)) {
            this._userControls.classList.remove(HtmlController.DisabledClass);
        }

        if (this._userControls_supreme != null) {
            this._userControls_supreme.style.display = "block";
        }

        if (ModuleManager.freespinsEnabled()) {
            ModuleManager.enableFreespins();
            this.startFeatureAutoStart();
            return;
        }

        this.prepareNewGameRound();
        ModuleManager.enableGamble();
    }

    intro(long: boolean = false): void {
        AppContext.clientHost.setLEDStripe(this.ledStripe, 2);

        this._unColectedWin = GameManager.gameResponse.totalWin;

        if (DataManager.GameRoundClosed) {
            DataManager.updateDataElement(GameData.DataBalanceBonus);
            DataManager.updateDataElement(GameData.DataBalanceReal);
            DataManager.updateDataElement(GameData.DataBalanceTotal);
        }

        if (long) {
            DataManager.gameState("PrimaryGameEnded");
            this._wasPrimaryGameEnded = true;
        }

        this._controller.intro(long);
        if (this._userControls != null) {
            this._userControls.style.display = "block";
        }

        if (this._userControls_supreme != null) {
            this._userControls_supreme.style.display = "block";
        }
    }

    introFinished(): void {
        if (GameManager.disableOnCollect) {
            this._autoplayAfterGamble = false;
            if (DataManager.GameRoundClosed) {
                GameManager.disableGamePlay = true;
            }
        }
        if (this._autoplayAfterGamble) {
            DataManager.Autoplay = this._autoplayAfterGamble;
        }

        if (this._autoplayAfterFeature) {
            DataManager.Autoplay = this._autoplayAfterFeature;
        }

        ModuleManager.enableGamble();
        this.prepareNewGameRound();
        if (DataManager.Autoplay) {
            if (this._autoplayAfterFeature) {
                this._autoplayDelay.start(1); // Because introFinished() is delayed by timer with duration 1.
            } else {
                this.autoplay();
            }
            this._autoplayAfterFeature = false;
        }
        if (this._userControls != null && this._userControls.classList.contains(HtmlController.DisabledClass)) {
            this._userControls.classList.remove(HtmlController.DisabledClass);
        }

        if (
            this._userControls_supreme != null &&
            this._userControls_supreme.classList.contains(HtmlController.DisabledClass)
        ) {
            this._userControls_supreme.classList.remove(HtmlController.DisabledClass);
        }
    }

    outro(): void {
        this._wasPrimaryGameEnded = true;
        this._wasGameEnded = true;
        this._afterWinDelay.stop();
        this._autoplayDelay.stop();

        if (ModuleManager.freespinsActive()) {
            this._autoplayAfterFeature = this._featureAutoStarted ? DataManager.Autoplay : false;
        }
        this._autoplayAfterGamble = DataManager.Autoplay;
        DataManager.Autoplay = false;
        this._featureAutoStarted = false;
        if (ModuleManager.freespinsActive()) {
            this._autoplayAfterGamble = false;
            if (this._userControls != null && !this._userControls.classList.contains(HtmlController.DisabledClass)) {
                this._userControls.classList.add(HtmlController.DisabledClass);
            }

            if (
                this._userControls_supreme != null &&
                !this._userControls_supreme.classList.contains(HtmlController.DisabledClass)
            ) {
                this._userControls_supreme.classList.add(HtmlController.DisabledClass);
            }
        }
        this._paytable.reset();
        this._help.reset();
        this._controller.outro();
    }

    outroFinished(): void {
        if (ModuleManager.freespinsActive()) {
            if (this._userControls != null) {
                this._userControls.style.display = "none";
            }

            if (this._userControls_supreme != null) {
                this._userControls_supreme.style.display = "none";
            }

            ModuleManager.introModule(GameModules.Freespins);
        } else {
            ModuleManager.introModule(GameModules.Gamble);
        }
    }

    prepareNewGameRound(): void {
        this._afterWinDelay.stop();

        if (GameManager.disableGamePlay) {
            DataManager.gameState("GamePlayDisabled");
            return;
        }

        if (DataManager.GameLimit) {
            if (DataManager.Autoplay) {
                DataManager.gameState("AutoPlayEnded");
            }
            DataManager.Autoplay = false;
            this.limit();
        } else {
            if (!DataManager.Autoplay) {
                let totalwin: boolean = GameManager.gameResponse.totalWin > 0;

                this.actions.changeActionStates({
                    [BaseActions.ActionAutoplay]: DataManager.autoplayEnabled(),
                    [BaseActions.ActionAutoplayStop]: false,
                    [BaseActions.ActionBaseCollect]: !DataManager.GameRoundClosed,
                    [BaseActions.ActionBetCycle]: DataManager.GameRoundClosed,
                    [BaseActions.ActionDenomSelect]: DataManager.GameRoundClosed,
                    [BaseActions.ActionCoinCountSelect]: DataManager.GameRoundClosed,
                    [BaseActions.ActionBetSelect]: DataManager.GameRoundClosed,
                    [BaseActions.ActionSpin]: true,
                    [BaseActions.ActionSpinStop]: false,
                    [BaseActions.ActionSelectPayline + 1]: DataManager.GameRoundClosed && !totalwin,
                    [BaseActions.ActionSelectPayline + 2]: DataManager.GameRoundClosed && !totalwin,
                    [BaseActions.ActionSelectPayline + 3]: DataManager.GameRoundClosed && !totalwin,
                    [BaseActions.ActionSelectPayline + 4]: DataManager.GameRoundClosed && !totalwin,
                    [BaseActions.ActionSelectPayline + 5]: DataManager.GameRoundClosed && !totalwin,
                    [BaseActions.ActionSelectPayline + 6]: DataManager.GameRoundClosed && !totalwin,
                    [BaseActions.ActionSelectPayline + 7]: DataManager.GameRoundClosed && !totalwin,
                    [BaseActions.ActionSelectPayline + 8]: DataManager.GameRoundClosed && !totalwin,
                    [BaseActions.ActionSelectPayline + 9]: DataManager.GameRoundClosed && !totalwin,
                    [BaseActions.ActionSelectPayline + 10]: DataManager.GameRoundClosed && !totalwin,
                    [BaseActions.ActionSelectPayline + 11]: DataManager.GameRoundClosed && !totalwin,
                    [BaseActions.ActionSelectPayline + 12]: DataManager.GameRoundClosed && !totalwin,
                    [BaseActions.ActionSelectPayline + 13]: DataManager.GameRoundClosed && !totalwin,
                    [BaseActions.ActionSelectPayline + 14]: DataManager.GameRoundClosed && !totalwin,
                    [BaseActions.ActionSelectPayline + 15]: DataManager.GameRoundClosed && !totalwin,
                });

                this.terminalActions.changeActionStates({
                    [TerminalActions.Payout]: GameManager.canExit(),
                    [TerminalActions.BackToLobby]: GameManager.canExit(),
                    [TerminalActions.StartWithCollect]: true,
                    [TerminalActions.Autoplay]: DataManager.autoplayEnabled(),
                    [TerminalActions.BetOrGamble]: ModuleManager.gambleEnabled() || DataManager.GameRoundClosed,
                });
            }
        }

        HtmlController.updateActionElements(this.getAllActions());
        AppContext.clientHost.actionsUpdated(this.getAllTerminalActions());
    }

    limit(): void {
        LimitDialog.openDialog();

        if (DataManager.GameRoundClosed) {
            this.actions.changeActionStates({
                [BaseActions.ActionAutoplay]: DataManager.autoplayEnabled(),
                [BaseActions.ActionAutoplayStop]: false,
                [BaseActions.ActionBaseCollect]: false,
                [BaseActions.ActionBetCycle]: true,
                [BaseActions.ActionDenomSelect]: true,
                [BaseActions.ActionCoinCountSelect]: true,
                [BaseActions.ActionBetSelect]: true,
                [BaseActions.ActionSpin]: true,
                [BaseActions.ActionSpinStop]: false,
            });
            this.terminalActions.changeActionStates({
                [TerminalActions.Payout]: GameManager.canExit(),
                [TerminalActions.BackToLobby]: GameManager.canExit(),
                [TerminalActions.StartWithCollect]: true,
                [TerminalActions.Autoplay]: DataManager.autoplayEnabled(),
                [TerminalActions.BetOrGamble]: true,
            });
        } else {
            this.actions.changeActionStates({
                [BaseActions.ActionAutoplay]: false,
                [BaseActions.ActionAutoplayStop]: false,
                [BaseActions.ActionBaseCollect]: true,
                [BaseActions.ActionBetCycle]: false,
                [BaseActions.ActionDenomSelect]: false,
                [BaseActions.ActionCoinCountSelect]: false,
                [BaseActions.ActionBetSelect]: false,
                [BaseActions.ActionSpin]: false,
                [BaseActions.ActionSpinStop]: false,
            });
            this.terminalActions.changeActionStates({
                [TerminalActions.Payout]: GameManager.canExit(),
                [TerminalActions.BackToLobby]: GameManager.canExit(),
                [TerminalActions.StartWithCollect]: true,
                [TerminalActions.Autoplay]: false,
                [TerminalActions.BetOrGamble]: false,
            });
        }
    }

    disable(): void {
        this._afterWinDelay.stop();
        this.actions.disableAllActions();
        this.terminalActions.disableAllActions();
        HtmlController.updateActionElements(this.getAllActions());
        AppContext.clientHost.actionsUpdated(this.getAllTerminalActions());
    }

    cycleHelp(): void {
        this._help.cycle();
    }

    /**
     * Set _autoplayAfterFeature.
     * @param tf True / False
     */
    public setAutoplayAfterFeature(tf: boolean): void {
        this._autoplayAfterFeature = tf;
    }

    /**
     * Start feature if enabled.
     */
    private featureAutoStartDelayed(): void {
        this._featureAutoStartTimer.stop();

        if (ModuleManager.freespinsEnabled()) {
            this._featureAutoStarted = true;
            ModuleManager.startFreespins();
        }
    }

    /**
     * Auto start feature.
     * Duration is set to preDuration + duration from configuration.
     * @param {number} preDuration
     */
    protected startFeatureAutoStart(preDuration: number = 0): void {
        if (AppContext.gameConfiguration.FeatureAutoPlay != null && AppContext.gameConfiguration.FeatureAutoPlay > 0) {
            this._featureAutoStartTimer.start(preDuration + AppContext.gameConfiguration.FeatureAutoPlay);
        }
    }

    primaryGameEnded(): boolean {
        return this._wasPrimaryGameEnded;
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/Channels/FireBird/FireBirdBaseUserControls.ts
 **/